import React, { useState, useEffect } from 'react';
import { subscriptionService } from '../../services/subscription.service';
import { Check, Loader2, ArrowLeft, Zap, Sparkles, ShieldCheck, CreditCard } from 'lucide-react';
import { Sidebar, Header, ToastContainer } from '../Dashboard/DashboardComponents';
import { authService } from '../../services/auth.service';
import { useNavigate } from 'react-router-dom';
import { motion } from 'framer-motion';
import { Button } from '../../components/ui/button';

const MONTHLY_PRICE_ID = process.env.REACT_APP_DODO_MONTHLY_PRODUCT_ID!;
const YEARLY_PRICE_ID = process.env.REACT_APP_DODO_YEARLY_PRODUCT_ID!;

const FEATURES = [
    'No watermarks on high-res exports',
    'Advanced effects & transitions',
    'Priority technical support',
];

const Pricing = () => {
    const [loading, setLoading] = useState<string | null>(null);
    const [sidebarOpen, setSidebarOpen] = useState(true);
    const [isMobile, setIsMobile] = useState(false);
    const [user, setUser] = useState<any>(null);
    const navigate = useNavigate();

    useEffect(() => {
        const checkScreenSize = () => {
            setIsMobile(window.innerWidth < 1024);
            setSidebarOpen(window.innerWidth >= 1024);
        };
        checkScreenSize();
        window.addEventListener('resize', checkScreenSize);
        authService.getProfile().then(setUser).catch(() => navigate('/login'));
        return () => window.removeEventListener('resize', checkScreenSize);
    }, [navigate]);

    const handleLogout = () => {
        authService.logout();
        navigate('/login');
    };

    const handleSubscribe = async (variantId: string) => {
        try {
            setLoading(variantId);
            const checkoutUrl = await subscriptionService.createCheckout(variantId);
            window.location.href = checkoutUrl;
        } catch (error) {
            console.error('Checkout failed:', error);
            setLoading(null);
        }
    };

    return (
        <div className="flex h-screen bg-slate-50 text-slate-900 overflow-hidden font-sans relative">
            <Sidebar
                isOpen={sidebarOpen}
                isMobile={isMobile}
                user={user}
                onToggle={() => setSidebarOpen(!sidebarOpen)}
                activePage="billing"
            />
            <div className="flex-1 flex flex-col overflow-hidden relative z-10">
                <Header
                    isMobile={isMobile}
                    onToggleSidebar={() => setSidebarOpen(!sidebarOpen)}
                    user={user}
                    onLogout={handleLogout}
                    title="Pricing Plans"
                />
                <ToastContainer />
                <main className="flex-1 overflow-y-auto custom-scrollbar p-6 lg:p-12">
                    <div className="max-w-5xl mx-auto space-y-12">

                        {/* Breadcrumb */}
                        <motion.button
                            initial={{ opacity: 0, x: -10 }}
                            animate={{ opacity: 1, x: 0 }}
                            onClick={() => navigate('/billing')}
                            className="flex items-center gap-2 text-sm font-semibold text-slate-500 hover:text-indigo-600 transition-all group"
                        >
                            <ArrowLeft className="w-4 h-4 group-hover:-translate-x-1 transition-transform" />
                            Back to Billing
                        </motion.button>

                        {/* Title Section */}
                        <div className="text-center lg:text-left">
                            <motion.h1
                                initial={{ opacity: 0, y: 20 }}
                                animate={{ opacity: 1, y: 0 }}
                                className="text-3xl lg:text-5xl font-extrabold text-slate-900 mb-4 tracking-tight"
                            >
                                Choose the plan that’s <span className="text-indigo-600">right for you.</span>
                            </motion.h1>
                            <motion.p
                                initial={{ opacity: 0, y: 20 }}
                                animate={{ opacity: 1, y: 0 }}
                                transition={{ delay: 0.1 }}
                                className="text-slate-500 text-lg font-medium max-w-2xl"
                            >
                                Simple, transparent pricing for creators of all levels. No hidden fees. Cancel or change your plan at any time.
                            </motion.p>
                        </div>

                        {/* Plans Grid */}
                        <div className="grid grid-cols-1 md:grid-cols-2 gap-8 pt-4">

                            {/* Monthly Plan */}
                            <motion.div
                                initial={{ opacity: 0, scale: 0.95 }}
                                animate={{ opacity: 1, scale: 1 }}
                                transition={{ delay: 0.2 }}
                                className="group relative flex flex-col p-8 lg:p-10 rounded-3xl bg-white border border-slate-200 hover:border-indigo-200 hover:shadow-xl transition-all duration-300 shadow-sm"
                            >
                                <div className="mb-8 relative z-10">
                                    <div className="flex items-center gap-2 mb-4">
                                        <div className="w-9 h-9 rounded-xl bg-slate-50 flex items-center justify-center">
                                            <CreditCard className="w-5 h-5 text-slate-400" />
                                        </div>
                                        <p className="text-sm font-bold text-slate-500">Monthly</p>
                                    </div>
                                    <div className="flex items-baseline gap-1">
                                        <span className="text-5xl font-bold text-slate-900">$15</span>
                                        <span className="text-slate-500 text-sm font-medium">/ month</span>
                                    </div>
                                    <p className="text-xs text-slate-400 font-medium mt-3 uppercase tracking-wide">Billed monthly. Ultimate flexibility.</p>
                                </div>

                                <ul className="space-y-4 mb-10 flex-1 relative z-10">
                                    {FEATURES.map((f, i) => (
                                        <li key={i} className="flex items-center gap-3 text-sm font-medium text-slate-600">
                                            <div className="w-5 h-5 rounded-full bg-slate-50 flex items-center justify-center shrink-0">
                                                <Check className="w-3 h-3 text-slate-400" strokeWidth={3} />
                                            </div>
                                            {f}
                                        </li>
                                    ))}
                                </ul>

                                <Button
                                    onClick={() => handleSubscribe(MONTHLY_PRICE_ID)}
                                    disabled={loading !== null}
                                    className="w-full h-12 rounded-xl text-sm font-bold bg-slate-900 hover:bg-slate-800 text-white transition-all duration-200 disabled:opacity-50"
                                >
                                    {loading === MONTHLY_PRICE_ID ? (
                                        <Loader2 className="w-5 h-5 animate-spin" />
                                    ) : (
                                        "Get Started Monthly"
                                    )}
                                </Button>
                            </motion.div>

                            {/* Yearly Plan — Highlighted */}
                            <motion.div
                                initial={{ opacity: 0, scale: 0.95 }}
                                animate={{ opacity: 1, scale: 1 }}
                                transition={{ delay: 0.3 }}
                                className="relative flex flex-col p-8 lg:p-10 rounded-3xl bg-white border-2 border-indigo-500 shadow-xl shadow-indigo-100 overflow-hidden group"
                            >
                                {/* Label Badge */}
                                <div className="absolute top-6 right-6">
                                    <span className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full bg-indigo-600 text-white text-[10px] font-bold uppercase tracking-wider shadow-lg">
                                        <Zap className="w-3 h-3 fill-white" />
                                        Save 34%
                                    </span>
                                </div>

                                <div className="mb-8 relative z-10">
                                    <div className="flex items-center gap-2 mb-4">
                                        <div className="w-9 h-9 rounded-xl bg-indigo-50 flex items-center justify-center">
                                            <Sparkles className="w-5 h-5 text-indigo-600" />
                                        </div>
                                        <p className="text-sm font-bold text-indigo-600">Yearly</p>
                                    </div>
                                    <div className="flex items-baseline gap-1">
                                        <span className="text-5xl font-bold text-slate-900">$119</span>
                                        <span className="text-slate-500 text-sm font-medium">/ year</span>
                                    </div>
                                    <p className="text-xs text-indigo-600 font-bold mt-3 uppercase tracking-wide">Only $9.91/month • Save $61 vs monthly</p>
                                </div>

                                <ul className="space-y-4 mb-10 flex-1 relative z-10">
                                    {FEATURES.map((f, i) => (
                                        <li key={i} className="flex items-center gap-3 text-sm font-medium text-slate-600">
                                            <div className="w-5 h-5 rounded-full bg-indigo-50 flex items-center justify-center shrink-0">
                                                <Check className="w-3 h-3 text-indigo-600" strokeWidth={3} />
                                            </div>
                                            {f}
                                        </li>
                                    ))}
                                    <li className="flex items-center gap-3 text-sm font-bold text-indigo-600 italic">
                                        <div className="w-5 h-5 rounded-full bg-indigo-600 flex items-center justify-center shrink-0">
                                            <Sparkles className="w-3 h-3 text-white fill-white" />
                                        </div>
                                        Early access to new features
                                    </li>
                                </ul>

                                <Button
                                    onClick={() => handleSubscribe(YEARLY_PRICE_ID)}
                                    disabled={loading !== null}
                                    className="w-full h-12 rounded-xl text-sm font-bold bg-indigo-600 hover:bg-indigo-700 text-white transition-all duration-200 disabled:opacity-50 shadow-md shadow-indigo-200"
                                >
                                    {loading === YEARLY_PRICE_ID ? (
                                        <Loader2 className="w-5 h-5 animate-spin" />
                                    ) : (
                                        "Subscribe Yearly — Best Deal"
                                    )}
                                </Button>
                            </motion.div>
                        </div>

                        {/* Footer Info */}
                        <div className="pt-8 border-t border-slate-100 flex flex-col md:flex-row items-center justify-between gap-6">
                            <div className="flex items-center gap-3">
                                <ShieldCheck className="w-6 h-6 text-slate-300" />
                                <p className="text-xs text-slate-500 font-medium max-w-[250px] text-left">
                                    Secure payments powered by Dodo Payments. Your data is always protected.
                                </p>
                            </div>
                            <div className="flex gap-8">
                                <div className="text-left">
                                    <p className="text-[10px] font-bold text-slate-400 uppercase tracking-wider mb-1">Flexibility</p>
                                    <p className="text-xs font-bold text-slate-700">Cancel Anytime</p>
                                </div>
                                <div className="text-left">
                                    <p className="text-[10px] font-bold text-slate-400 uppercase tracking-wider mb-1">Support</p>
                                    <p className="text-xs font-bold text-slate-700">24/7 Access</p>
                                </div>
                            </div>
                        </div>
                    </div>
                </main>
            </div>
        </div>
    );
};

export default Pricing;
